import {useEffect, useState} from 'react';
import {useBetween} from 'use-between';
import {acceptRequestForLinking, declineRequestForLinking, getLinkedProfiles, requestLinkAccount} from './api';
import {CounterpartyLinkedProfile, requestForLinkingId, requestIdentificator} from './Types';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Platform} from 'react-native';
import {AnyAction, Dispatch} from 'redux';
import {StorageKeys} from '../../storage';
import {profileSlice} from '../../../redux/reducers/profile';
import {setRuntimeLoginStepState} from '../../../redux/actions/runtime/loginStep';
import {resetLoginState} from '../../../redux/actions/login';
import {LoginState} from '../../models/login/loginStep';
import {AppActions} from '../../../redux/actions';
import {addNotification} from '../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../components/snackbar/model';

export interface ParentAccount {
  token: string | null;
  fullName: string;
}

const NON_ACTIVATED_STATUSES = [5, 6, undefined];

const LinkedAccountsStateHandler = () => {
  const [linkedAccounts, setLinkedAccounts] = useState<CounterpartyLinkedProfile[]>([]);
  const [parentAccount, setParentAccountState] = useState<ParentAccount | null>();
  const storage = Platform.OS === 'web' ? localStorage : AsyncStorage;
  const [isLoading, setIsLoading] = useState(false);

  const loadLinkedAccountsInfo = async () => {
    setIsLoading(true);
    const linkedAccountsResponse = await getLinkedProfiles().finally(() => setIsLoading(false));
    setLinkedAccounts(linkedAccountsResponse);
    return linkedAccountsResponse;
  };

  const declineLinkRequest = async (id: requestForLinkingId) => {
    setIsLoading(true);
    await declineRequestForLinking(id).finally(() => setIsLoading(false));
    setLinkedAccounts((prev) => {
      const linkedAccountsNew = prev.filter((linkedAccount) => linkedAccount.id.toString() !== id);
      return linkedAccountsNew;
    });
  };
  const requestLinkingToAccount = async (identificator: requestIdentificator) => {
    try {
      setIsLoading(true);
      await requestLinkAccount(identificator);
      await loadLinkedAccountsInfo();
    } finally {
      setIsLoading(false);
    }
  };

  const acceptLinkingRequest = async (id: requestForLinkingId, dispatch: Dispatch) => {
    try {
      setIsLoading(true);
      await acceptRequestForLinking(id);
      await loadLinkedAccountsInfo();
    } finally {
      setIsLoading(false);
      dispatch(
        addNotification({
          message: 'Аккаунт успешно связан',
          type: SnackbarNotificationType.Success,
        }) as unknown as AnyAction,
      );
    }
  };

  const resetLinkingAccountState = () => {
    setLinkedAccounts([]);
    storage.removeItem('parent');
    setParentAccountState(null);
  };

  const setParentAccount = async (parentName: string) => {
    const parentToken = await storage.getItem('token');
    const parent: ParentAccount = {
      token: JSON.parse(parentToken!), // since token saved as stringified
      fullName: parentName,
    };
    await storage.setItem('parent', JSON.stringify(parent));
    setParentAccountState(parent);
  };

  const getParentAccount = async () => {
    const loadedParent = await storage.getItem('parent');
    const parentAccountFromStorage = (loadedParent ? JSON.parse(loadedParent) : null) as ParentAccount;
    setParentAccountState(parentAccountFromStorage);
  };

  const switchToLinkedAccount = async (token: string, dispatch: Dispatch<any>) => {
    try {
      setIsLoading(true);
      const tokenToReplace = JSON.stringify(token);
      if (token === parentAccount?.token) resetLinkingAccountState(); // means we switch to parent account
      await dispatch({type: AppActions.Reset});
      await storage.setItem(StorageKeys.token, tokenToReplace);
      await storage.removeItem(StorageKeys.isESIA);
      if (Platform.OS === 'web') {
        await dispatch(profileSlice.actions.setAuthenticationState(true));
      } else {
        await dispatch(profileSlice.actions.setPinCode(null));
      }
      await dispatch(profileSlice.actions.setToken(tokenToReplace));
      await dispatch(setRuntimeLoginStepState(LoginState.enter));
      await dispatch(resetLoginState());
      await dispatch(profileSlice.actions.setSessionState(false));
      await loadLinkedAccountsInfo();
    } finally {
      setIsLoading(false);
    }
  };

  const [isNeedActivation, setIsNeedActivation] = useState<boolean>();
  useEffect(() => {
    const status = linkedAccounts.some((account) =>
      account?.cardNumber == null || NON_ACTIVATED_STATUSES.includes(account?.cardStatusId) ? true : false,
    );
    setIsNeedActivation(status);
  }, [linkedAccounts]);

  const getActivationStatus = () => isNeedActivation;

  return {
    loadLinkedAccountsInfo,
    linkedAccounts,
    declineLinkRequest,
    requestLinkingToAccount,
    acceptLinkingRequest,
    resetLinkingAccountState,
    setParentAccount,
    getParentAccount,
    parentAccount,
    switchToLinkedAccount,
    isLoading,
    setIsLoading,
    getActivationStatus,
  };
};

export const useAccountLinker = () => useBetween(LinkedAccountsStateHandler);

// Mockdata to initalState of linkedAccounts to function without needed endpoint
// {
//   id: 5,
//   counterpartyFullName: 'string string string',
//   status: 'Accepted',
//   cardNumber: '9156',
//   createdAt: '2024-02-27T09:46:15.803033',
//   isCardHolderParent: false,
// },
// {
//   id: 6,
//   counterpartyFullName: 'Test Test Ku',
//   status: 'Pending',
//   cardNumber: '6666',
//   createdAt: '2024-02-27T09:46:15.803033',
//   isCardHolderParent: true,
// },
// {
//   id: 7,
//   counterpartyFullName: 'Kek lol KEEEEEEK',
//   status: 'Pending',
//   cardNumber: '7777',
//   createdAt: '2024-02-27T09:46:15.803033',
//   isCardHolderParent: false,
// },
// {
//   id: 8,
//   counterpartyFullName: '!!!Kek lol KEEEEEEK',
//   status: 'Accepted',
//   cardNumber: '7777',
//   createdAt: '2024-02-27T09:46:15.803033',
//   isCardHolderParent: false,
// },
